import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { notificationStore } from "@/store/api/notificationStore";
import type { TransactionData } from "@/types/types";

interface RemindPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ligne relancée — porte userId et client. */
  tx: TransactionData | null;
  /** Message pré-rempli (montant restant interpolé), calculé par l'appelant. */
  defaultMessage: string;
}

/**
 * Relance d'un impayé : envoie une notification ADMIN_MESSAGE à l'adhérent
 * (payload.title / payload.text — les seuls champs lus par l'edge function
 * send_notifications ; tout autre type y serait jeté silencieusement).
 */
export function RemindPaymentDialog({
  open,
  onOpenChange,
  tx,
  defaultMessage,
}: RemindPaymentDialogProps) {
  const { t } = useTranslation(["payments", "common"]);
  const { toast } = useToast();
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  // Resynchroniser le texte à chaque ouverture (nouvelle ligne = nouveau
  // message par défaut) ; les éditions d'une ouverture précédente sont jetées.
  useEffect(() => {
    if (open) setMessage(defaultMessage);
  }, [open, defaultMessage]);

  const onSend = async () => {
    if (!tx?.userId || !message.trim() || sending) return;
    setSending(true);
    try {
      const res = await notificationStore.createAdminMessage(tx.userId, {
        title: t("remind.notificationTitle", "Rappel de paiement"),
        text: message.trim(),
      });
      if (!res.ok) {
        toast({
          title: t("common:error"),
          description: t("remind.errorDesc", "La notification n'a pas pu être créée."),
          variant: "destructive",
        });
        return; // le dialogue reste ouvert, le texte est conservé
      }
      toast({
        title: t("remind.successTitle", "Rappel envoyé"),
        description: t("remind.successDesc", { client: tx.client }),
      });
      onOpenChange(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>{t("remind.title", "Relancer le client")}</DialogTitle>
          <DialogDescription>
            {t("remind.description", { client: tx?.client ?? "" })}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-[100px] text-base md:text-sm"
          disabled={sending}
        />
        <DialogFooter className="flex flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            {t("common:cancel")}
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={onSend}
            disabled={!message.trim() || sending}
          >
            {sending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            {t("remind.send", "Envoyer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
